import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import useShortcuts from "src/hooks/useShortcuts"
import { DEFAULT_SHORTCUTS, SHORTCUT_ACTIONS, keycapLabel, labelKeyFor, rebind, saveShortcuts } from "src/utils/shortcuts"

import "./ShortcutSettings.css"

/* Rebind the map's order keys, and the one for ending the round. Click a keycap, press the key you
   want; Backspace clears it, Esc leaves it. Saved at once — there is no form to submit — and the
   action bar and the top bar pick it up. */
const ShortcutSettings = () => {
  const { t } = useTranslation()
  const shortcuts = useShortcuts()
  const [listening, setListening] = useState(null)

  useEffect(() => {
    if (!listening) return undefined
    const onKeyDown = (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (e.key === "Escape") {
        setListening(null)
        return
      }
      if (e.key === "Backspace" || e.key === "Delete") {
        saveShortcuts(rebind(shortcuts, listening, null))
        setListening(null)
        return
      }
      if (e.key.length !== 1 || e.metaKey || e.ctrlKey || e.altKey) return
      saveShortcuts(rebind(shortcuts, listening, e.key))
      setListening(null)
    }
    // Capture phase, so the modal's own Escape handling and the bar's shortcuts do not also fire.
    window.addEventListener("keydown", onKeyDown, true)
    return () => window.removeEventListener("keydown", onKeyDown, true)
  }, [listening, shortcuts])

  const isDefault = SHORTCUT_ACTIONS.every((action) => shortcuts[action] === DEFAULT_SHORTCUTS[action])

  return (
    <div className="shortcut-settings">
      <div className="shortcut-settings-head">
        <span className="section-label">{t("settings.shortcuts")}</span>
        <button
          type="button"
          className="shortcut-reset"
          onClick={() => saveShortcuts({ ...DEFAULT_SHORTCUTS })}
          disabled={isDefault}
        >
          {t("settings.shortcutsReset")}
        </button>
      </div>
      <p className="p-mini shortcut-help">
        {listening ? t("settings.shortcutsListening") : t("settings.shortcutsHelp")}
      </p>
      <ul className="shortcut-list">
        {SHORTCUT_ACTIONS.map((action) => {
          const name = t(labelKeyFor(action))
          return (
            <li key={action} className="shortcut-row">
              <span className="shortcut-name">{name}</span>
              <button
                type="button"
                className={`shortcut-key${listening === action ? " listening" : ""}${shortcuts[action] ? "" : " empty"}`}
                onClick={() => setListening(listening === action ? null : action)}
                aria-label={t("settings.shortcutFor", { action: name })}
              >
                {listening === action ? "…" : keycapLabel(shortcuts[action]) || t("settings.shortcutNone")}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default ShortcutSettings
